import React from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Phone, Zap, Search, Wrench, Power, Plug, ShieldCheck,
  ArrowRight, CircleCheck, Clock, Flame,
} from "lucide-react";
import { SITE } from "../lib/site";
import { useLocation as useGeo } from "../contexts/LocationContext";
import SectionHeading from "../components/SectionHeading";
import useDocumentTitle from "../hooks/useDocumentTitle";

const TROUBLE_IMG = "https://images.pexels.com/photos/34054464/pexels-photo-34054464.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const URGENCES = [
  { icon: Zap, title: "Panne électrique générale", desc: "Plus de courant dans tout le logement, identification de la cause et remise en service." },
  { icon: Power, title: "Disjoncteur qui saute", desc: "Diagnostic du circuit défaillant, recherche du court-circuit et réparation." },
  { icon: Flame, title: "Court-circuit", desc: "Localisation, sécurisation et réparation rapide de la zone concernée." },
  { icon: Search, title: "Recherche de panne", desc: "Méthode rigoureuse pour identifier l'origine d'une panne complexe." },
  { icon: Plug, title: "Prise ou interrupteur HS", desc: "Remplacement, vérification du câblage et de la sécurité." },
  { icon: ShieldCheck, title: "Remise en service", desc: "Après sinistre, dégât des eaux ou coupure prolongée — remise en conformité." },
];

const STEPS = [
  { n: "01", title: "Appel & Diagnostic", desc: "Vous nous appelez, nous identifions le problème par téléphone et estimons le délai d'arrivée." },
  { n: "02", title: "Intervention rapide", desc: "Nous arrivons sous 30 minutes en moyenne en zone urbaine, équipés de tout le matériel nécessaire." },
  { n: "03", title: "Réparation & Sécurité", desc: "Diagnostic précis, réparation aux normes et tests complets avant remise en service." },
  { n: "04", title: "Devis transparent", desc: "Tarifs clairs annoncés avant intervention. Pas de mauvaise surprise sur la facture." },
];

export default function Depannage() {
  const { displayCity, city } = useGeo();
  useDocumentTitle(
    city
      ? `Dépannage électrique à ${city} — Urgence 24/7 | Voltis Électricité`
      : "Dépannage électrique d'urgence — Voltis Électricité",
    city
      ? `Électricien d'urgence à ${city} : panne, court-circuit, disjoncteur. Intervention 24/7, devis gratuit.`
      : "Électricien d'urgence : panne, court-circuit, disjoncteur. Intervention 24/7, devis gratuit."
  );
  return (
    <div data-testid="depannage-page">
      {/* HERO */}
      <section className="bg-slate-50">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16 py-16 lg:py-24">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center self-start gap-2 bg-red-500 text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-widest mb-7">
              <span className="emergency-dot" /> Urgence {SITE.emergency}
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              {city ? (
                <>
                  Dépannage électrique à{" "}
                  <span className="text-[#EAB308]">{city}.</span>
                </>
              ) : (
                <>
                  Dépannage électrique{" "}
                  <span className="text-[#EAB308]">près de chez vous.</span>
                </>
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              {city ? (
                <>
                  Vous recherchez un électricien à <strong className="text-slate-900">{city}</strong> pour
                  un dépannage rapide ? Panne, court-circuit, disjoncteur qui saute — nous intervenons
                  jour et nuit, 7j/7.
                </>
              ) : (
                <>
                  Vous recherchez un électricien <strong className="text-slate-900">près de chez vous</strong> pour
                  un dépannage rapide ? Panne, court-circuit, disjoncteur qui saute — nous intervenons
                  jour et nuit, 7j/7.
                </>
              )}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={`tel:${SITE.phoneTel}`} data-testid="depannage-call-btn" className="btn-primary text-base">
                <Phone className="w-5 h-5" /> Appeler maintenant
              </a>
              <Link to="/contact" className="btn-outline text-base">
                Demander un devis <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border-l-4 border-red-500">
              <img src={TROUBLE_IMG} alt="Dépannage électrique avec multimètre" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* URGENCES LIST */}
      <section className="section-padding bg-white">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nos interventions"
            title="Ce que nous dépannons."
            description="Toutes les urgences électriques courantes — du simple disjoncteur récalcitrant à la panne complexe."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {URGENCES.map((u) => (
              <div
                key={u.title}
                data-testid={`urgence-${u.title.toLowerCase().replace(/[^a-z]/g, '-').slice(0, 24)}`}
                className="border border-slate-200 p-7 hover:border-slate-900 transition-colors bg-white"
              >
                <span className="inline-flex w-12 h-12 items-center justify-center bg-red-500 text-white mb-5">
                  <u.icon className="w-5 h-5" strokeWidth={2} />
                </span>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">{u.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section-padding bg-slate-50">
        <div className="container-x">
          <SectionHeading
            eyebrow="Comment ça se passe"
            title="Un dépannage simple, en 4 étapes."
          />
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 p-7">
                <div className="font-heading text-5xl font-bold text-[#EAB308] mb-3">{s.n}</div>
                <h4 className="font-heading text-lg font-semibold text-slate-900 mb-2">{s.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GUARANTEES */}
      <section className="section-padding bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Nos engagements"
              title="Une intervention sérieuse, rapide et garantie."
            />
            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {[
                { icon: Clock, title: "30 min en moyenne", desc: "Délai d'arrivée en zone urbaine." },
                { icon: ShieldCheck, title: "Garantie 2 ans", desc: "Sur toutes les pièces et la main-d'œuvre." },
                { icon: CircleCheck, title: "Devis avant travaux", desc: "Pas de mauvaises surprises." },
                { icon: Wrench, title: "Matériel professionnel", desc: "Équipements certifiés et adaptés." },
              ].map((f) => (
                <div key={f.title} className="flex gap-4">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center bg-[#EAB308] text-slate-900">
                    <f.icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{f.title}</h4>
                    <p className="text-sm text-slate-600">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 bg-[#0F172A] text-white p-8 lg:p-10">
            <AlertTriangle className="w-10 h-10 text-[#EAB308] mb-5" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl lg:text-3xl font-bold mb-4">
              Une panne en cours ?
            </h3>
            <p className="text-slate-300 leading-relaxed mb-7">
              Ne tentez pas de réparer vous-même. Coupez le courant général
              et appelez-nous immédiatement.
            </p>
            <a href={`tel:${SITE.phoneTel}`} className="btn-primary justify-center w-full">
              <Phone className="w-5 h-5" /> {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
